import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  PasswordStrengthMeter,
  describeAuthFormError,
  formErrorClassName,
  formFieldClassName,
  formInputClassName,
  linkClassName,
  secondaryTextClassName,
  useAuth,
  validationErrorClassName,
  type AuthFormError,
} from '@monecolier/ui';
import { getMe, getPrivacyPolicy } from '@monecolier/api-client';
import { AccessDenied } from '../components/AccessDenied';
import {
  INVITATION_CONTINUATION_STATE,
  acceptInvitationPath,
  readInvitationToken,
  withInvitationToken,
} from '../lib/invitationContinuation';

const REDIRECT_AFTER_REGISTER = '/';

// PAR-01 : URL du portail parent (web-parent), pour le lien croisé "Je suis parent"
// ci-dessous — voir docs/history/PAR.md, section "PAR-01", pour le schéma complet de cette
// variable (repo variable GitHub + build-args + ARG/ENV Containerfile, même
// discipline que VITE_API_BASE_URL). Absente en environnement de test (jsdom, pas de
// build Vite réel) — repli sur une chaîne vide, le lien reste alors non fonctionnel
// mais ne casse jamais le rendu.
const PARENT_APP_URL = import.meta.env.VITE_PARENT_APP_URL ?? '';

// Miroir de apps/api/src/lib/password.ts#MIN_PASSWORD_LENGTH — les deux apps
// front-end n'ont pas d'accès direct au code d'apps/api (aucun package partagé entre
// eux), donc cette constante est dupliquée volontairement, comme d'autres valeurs
// synchronisées à la main dans ce monorepo (voir packages/types, énumérations
// alignées sur schema.prisma). Ce n'est qu'une pré-validation UX : le backend reste
// la seule source de vérité qui rejette réellement un mot de passe trop court.
const MIN_PASSWORD_LENGTH = 8;

export function Register() {
  // `apiClient` réutilisé tel quel depuis le contexte — voir le commentaire
  // équivalent dans Login.tsx.
  const { register, login, logout, apiClient } = useAuth();
  const navigate = useNavigate();
  // AUT-32 : présent UNIQUEMENT quand on arrive depuis le bouton "Créer un
  // compte" d'AcceptInvitation.tsx — absent, ce formulaire reste inchangé.
  const [searchParams] = useSearchParams();
  const invitationToken = readInvitationToken(searchParams);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // AUT-17 : deux champs SÉPARÉS (plus de "Nom complet" unique) — le
  // serveur en dérive `fullName`, jamais l'inverse.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [error, setError] = useState<AuthFormError | null>(null);
  // AUT-03 : même garde qu'à la connexion — voir Login.tsx.
  const [accessDenied, setAccessDenied] = useState(false);
  // CMP-02 : JAMAIS pré-cochée (état initial `false`, exigence explicite du
  // ticket) — voir apps/api/src/lib/consent.ts pour le texte servi ci-dessous
  // (PLACEHOLDER de développement, le PO fournira le texte légal définitif
  // séparément).
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [policyText, setPolicyText] = useState<string | null>(null);
  const [policyExpanded, setPolicyExpanded] = useState(false);

  useEffect(() => {
    getPrivacyPolicy(apiClient)
      .then((policy) => setPolicyText(policy.text))
      .catch(() => {
        // Best-effort : un échec de chargement du texte n'empêche PAS l'affichage
        // du reste du formulaire — la case reste utilisable (le backend revalide
        // `consentAccepted` de toute façon), seul le texte lui-même resterait
        // indisponible à la lecture avant de cocher.
      });
    // Chargement UNIQUE au montage — `apiClient` référence stable
    // (packages/ui/src/auth/AuthContext.tsx), aucune autre dépendance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setValidationError(null);

    // Validation côté client AVANT tout appel réseau — miroir de la validation
    // backend (apps/api/src/routes/auth.ts), mais appliquée ici sans attendre un
    // aller-retour HTTP pour un cas qu'on peut détecter localement.
    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`);
      return;
    }

    // CMP-02 : miroir CLIENT de la validation backend (POST /auth/register, 400
    // consent_required) — le backend reste la seule source de vérité qui refuse
    // réellement l'inscription, cette pré-validation évite juste un aller-retour
    // réseau inutile pour un cas détectable localement.
    if (!consentAccepted) {
      setValidationError('Vous devez accepter la politique de confidentialité pour créer un compte.');
      return;
    }

    setSubmitting(true);
    try {
      await register({ email, password, firstName, lastName, consentAccepted });
      // Inscription -> connexion immédiate avec les mêmes identifiants : le backend
      // active le compte tout de suite (décision AUT-01, pas de vérification email en
      // V1) — pas de raison de faire retaper l'email/mot de passe qu'on vient de saisir.
      const result = await login({ email, password });
      // AUT-05 : un compte tout juste créé n'a JAMAIS le MFA activé (il faut déjà
      // être authentifié pour l'activer, voir POST /mfa/totp/setup) — ce cas est
      // donc structurellement impossible ici, garde défensive uniquement pour
      // TypeScript (login() reste partagé avec le formulaire /login, qui LUI peut
      // légitimement recevoir ce défi).
      if ('mfaRequired' in result) {
        throw new Error("Un compte fraîchement créé ne peut pas avoir le MFA activé.");
      }
      // CMP-02 : même raisonnement — l'inscription vient d'enregistrer un
      // consentement pour la version ACTUELLE (POST /auth/register, même
      // transaction), une re-sollicitation immédiate est donc structurellement
      // impossible ici aussi, garde défensive uniquement pour TypeScript.
      if ('consentRequired' in result) {
        throw new Error('Un compte fraîchement créé ne peut pas nécessiter un nouveau consentement.');
      }
      // AUT-32 : inscription depuis un lien d'invitation — retour direct sur
      // /accept-invitation, qui accepte l'invitation puis redirige vers l'accueil.
      // Surtout PAS de `GET /me` ici : le compte n'a encore aucun Membership, il
      // afficherait `AccessDenied` (écran "Créer mon établissement") à un employé
      // qui rejoint une école existante.
      if (invitationToken) {
        navigate(acceptInvitationPath(invitationToken), { replace: true, state: INVITATION_CONTINUATION_STATE });
        return;
      }
      // AUT-03 : mêmes règles qu'à la connexion directe — un compte fraîchement créé
      // n'a par construction aucun Membership tant que SCH-01/AUT-06 n'existent pas
      // (voir docs/architecture.md, section "AUT-03") : ce cas se produira donc pour
      // TOUTE inscription en usage réel aujourd'hui — attendu, pas un bug.
      const me = await getMe(apiClient, result.accessToken);
      if (me.role === null) {
        setAccessDenied(true);
      } else {
        navigate(REDIRECT_AFTER_REGISTER);
      }
    } catch (err) {
      // describeAuthFormError() journalise TOUJOURS l'erreur réelle via
      // console.error avant de la traduire — voir packages/ui/src/auth/
      // authFormError.ts pour le bug réel (CORS silencieux) que cette
      // journalisation permanente permet désormais de diagnostiquer. Le message
      // backend d'un 409 ("Un compte existe déjà avec cet email.") est déjà
      // clair — repris tel quel, jamais reformulé.
      setError(describeAuthFormError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (accessDenied) {
    return (
      <AccessDenied
        onLogout={() => {
          logout();
          setAccessDenied(false);
        }}
      />
    );
  }

  return (
    <Card title="Inscription — Portail École">
      {/* PAR-01 : différenciation explicite du parcours dès l'inscription — un
          parent qui atterrit ici (page d'accueil par défaut, voir FND-23) doit
          pouvoir repartir vers le bon portail avant de remplir un formulaire
          destiné à un représentant d'école. */}
      <div className="flex gap-2 mb-2" role="group" aria-label="Type de compte">
        <span
          aria-current="true"
          data-testid="register-portal-school"
          className="rounded-full bg-brand-100 px-3 py-1 text-sm font-medium text-brand-900"
        >
          Je représente une école
        </span>
        <a
          href={PARENT_APP_URL ? `${PARENT_APP_URL}/register` : '#'}
          data-testid="register-portal-parent-link"
          className={linkClassName}
        >
          Je suis parent
        </a>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className={formFieldClassName}>
          Prénom
          <input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            required
            autoComplete="given-name"
            data-testid="register-firstname-input"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          Nom
          <input
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            required
            autoComplete="family-name"
            data-testid="register-lastname-input"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
            className={formInputClassName}
          />
        </label>
        <label className={formFieldClassName}>
          Mot de passe
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="new-password"
            className={formInputClassName}
          />
        </label>
        <PasswordStrengthMeter password={password} />
        {/* CMP-02 : case JAMAIS pré-cochée, texte affiché intégralement au clic sur
            "Lire" (pas un simple lien de sortie — le consentement doit rester
            possible sans quitter le formulaire). */}
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(event) => setConsentAccepted(event.target.checked)}
              data-testid="register-consent-checkbox"
            />
            <span>
              J&rsquo;accepte la{' '}
              <button
                type="button"
                onClick={() => setPolicyExpanded((expanded) => !expanded)}
                className={linkClassName}
                data-testid="register-consent-toggle"
              >
                politique de confidentialité
              </button>
              .
            </span>
          </label>
          {policyExpanded ? (
            <div
              className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
              data-testid="register-consent-policy-text"
            >
              {policyText ?? 'Chargement…'}
            </div>
          ) : null}
        </div>
        {validationError ? (
          <p role="alert" data-testid="register-validation-error" className={validationErrorClassName}>
            {validationError}
          </p>
        ) : null}
        {error ? (
          <p role="alert" data-testid="register-error" className={formErrorClassName(error.kind)}>
            {error.message}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting || !consentAccepted}>
          {submitting ? 'Création...' : 'Créer mon compte'}
        </Button>
      </form>
      <p className={secondaryTextClassName}>
        Déjà un compte ? <Link to={withInvitationToken('/login', invitationToken)} className={linkClassName}>Se connecter</Link>
      </p>
    </Card>
  );
}
